import { useCallback, useEffect, useRef, useState, type RefObject } from "react";
import {
  DialogueQorumDrawer,
  type DialogueQorumHandle,
} from "./DialogueQorumDrawer";
import { StabiloToolbar } from "./StabiloToolbar";
import { useTextSelection } from "@/hooks/useTextSelection";
import type { DialogueAction } from "@/hooks/useDialogueQorum";

interface Props {
  conversationId: string;
  verdictText: string;
  containerRef: RefObject<HTMLElement | null>;
}

const HIGHLIGHT_NAME = "qorum-stabilo";

// Lazy detect support for the CSS Custom Highlight API.
// Lazy detect support for the CSS Custom Highlight API.
function hasHighlightApi(): boolean {
  if (typeof window === "undefined") return false;
  const cssAny = (typeof CSS !== "undefined" ? CSS : null) as unknown as
    | { highlights?: Map<string, unknown> }
    | null;
  return (
    !!cssAny?.highlights &&
    typeof (window as unknown as { Highlight?: unknown }).Highlight ===
      "function"
  );
}


/**
 * V2 Dialogue Qorum :
 * - Détecte les sélections de texte dans `containerRef`
 * - Affiche une toolbar contextuelle <StabiloToolbar />
 * - Persiste un surlignage fluo doré via la CSS Custom Highlight API
 * - Reroute l'action vers le drawer existant (qui garde son bouton fallback)
 */
export function DialogueQorumSelectionLayer({
  conversationId,
  verdictText,
  containerRef,
}: Props) {
  const drawerRef = useRef<DialogueQorumHandle>(null);
  const selection = useTextSelection(containerRef);
  const [persistentRanges, setPersistentRanges] = useState<Range[]>([]);

  // Sync persistent ranges with the CSS Custom Highlight registry.
  useEffect(() => {
    if (!hasHighlightApi()) return;
    const W = window as unknown as { Highlight: new (...r: Range[]) => unknown };
    const registry = (CSS as unknown as { highlights: Map<string, unknown> })
      .highlights;

    if (persistentRanges.length === 0) {
      registry.delete(HIGHLIGHT_NAME);
      return;
    }
    const highlight = new W.Highlight(...persistentRanges);
    registry.set(HIGHLIGHT_NAME, highlight);
    return () => {
      registry.delete(HIGHLIGHT_NAME);
    };
  }, [persistentRanges]);

  const handleAction = useCallback(
    (action: DialogueAction) => {
      if (!selection) return;
      const { text } = selection;
      drawerRef.current?.openWithAction(action, text);
      // Ephemeral UI only: no persistent highlight, no DOM mutation.
      setPersistentRanges([]);
      window.getSelection()?.removeAllRanges();
    },
    [selection],
  );

  return (
    <>
      <StabiloToolbar selection={selection} onAction={handleAction} />
      <DialogueQorumDrawer
        ref={drawerRef}
        conversationId={conversationId}
        verdictText={verdictText}
      />
      <style>{`
        ::highlight(${HIGHLIGHT_NAME}) {
          background-color: rgba(168, 136, 75, 0.35);
          color: inherit;
        }
        [data-stabilo-target] ::selection,
        [data-stabilo-target]::selection {
          background-color: rgba(168, 136, 75, 0.35);
          color: inherit;
        }
      `}</style>
    </>
  );
}

export default DialogueQorumSelectionLayer;
